// ═══════════════════════════════════════════════════════════════════
//  parseQuery — Tokenize search input into structured clauses.
// ═══════════════════════════════════════════════════════════════════
//
//  Supports a small DSL:
//   - free terms:      `webhook auth`            → AND'd
//   - quoted phrase:   `"exact phrase"`          → literal substring
//   - negation:        `webhook -internal`       → exclude internal
//   - field filter:    `name:foo` or `subject:bar` → only that field
//   - type filter:     `type:webhook`            → block-type filter
//   - source filter:   `source:workflow`,
//                      `source:atlas`,
//                      `source:wf:<id>`          → restrict scope
//
//  Unknown tokens fall through as free terms — the parser is forgiving.
//
// ═══════════════════════════════════════════════════════════════════

import type { SearchSourceType } from '@/types/search';

export interface ParsedQuery {
  /** Free positive substrings — must all match. */
  positiveTerms: string[];
  /** Phrases (quoted). Stored without quotes; matched as literal substring. */
  phrases: string[];
  /** Negative substrings — must NOT match. */
  negativeTerms: string[];
  /** Field filters: only fields with these names are considered. */
  fieldFilters: Array<{ field: string; value: string }>;
  /** Block-type filter (e.g. webhook, send-email). */
  typeFilter: string | null;
  /** Source filter — coarse ('workflow' | 'atlas') or specific ('wf:<id>'). */
  sourceFilter: { kind: 'all' | 'workflows' | 'atlas' | 'specific'; sourceId?: string } | null;
  /**
   * Source-type narrowing inferred from `source:<source-type>`. Lets a
   * power user say `source:atlas-doc` to scope to docs only.
   */
  sourceTypeFilter: SearchSourceType[] | null;
  /** Whether the user explicitly enabled regex mode via `re:` prefix. */
  regex: boolean;
  /**
   * Canonical query string used for cache keys. Whitespace-normalized.
   */
  canonical: string;
  /** True when the parsed query is meaningful (≥ 1 positive clause). */
  hasContent: boolean;
}

const SOURCE_TYPE_VALUES: SearchSourceType[] = [
  'workflow-block',
  'workflow-connector',
  'workflow-group',
  'workflow-sheet',
  'workflow-cross-sheet-connector',
  'workflow-meta',
  'atlas-doc',
  'atlas-sheet',
  'atlas-ref',
  'atlas-category',
];

export function parseQuery(input: string): ParsedQuery {
  const out: ParsedQuery = {
    positiveTerms: [],
    phrases: [],
    negativeTerms: [],
    fieldFilters: [],
    typeFilter: null,
    sourceFilter: null,
    sourceTypeFilter: null,
    regex: false,
    canonical: '',
    hasContent: false,
  };

  const tokens = tokenize(input);
  for (const tok of tokens) {
    if (tok.startsWith('"') && tok.endsWith('"') && tok.length >= 2) {
      const phrase = tok.slice(1, -1);
      if (phrase) out.phrases.push(phrase);
      continue;
    }
    if (tok.startsWith('-') && tok.length > 1) {
      out.negativeTerms.push(tok.slice(1).toLowerCase());
      continue;
    }
    if (tok === 're:on' || tok === 're:true') {
      out.regex = true;
      continue;
    }
    const colonIdx = tok.indexOf(':');
    if (colonIdx > 0 && colonIdx < tok.length - 1) {
      const key = tok.slice(0, colonIdx).toLowerCase();
      const value = tok.slice(colonIdx + 1);
      if (key === 'type') {
        out.typeFilter = value.toLowerCase();
        continue;
      }
      if (key === 'source') {
        out.sourceFilter = parseSourceValue(value);
        const stf = sourceTypeFromValue(value);
        if (stf) out.sourceTypeFilter = [stf];
        continue;
      }
      // Otherwise treat as field filter.
      out.fieldFilters.push({ field: key, value });
      continue;
    }
    out.positiveTerms.push(tok.toLowerCase());
  }

  out.canonical = tokens.join(' ').trim().toLowerCase();
  out.hasContent =
    out.positiveTerms.length > 0 ||
    out.phrases.length > 0 ||
    out.fieldFilters.length > 0 ||
    out.typeFilter !== null;
  return out;
}

function parseSourceValue(value: string): ParsedQuery['sourceFilter'] {
  const v = value.toLowerCase();
  if (v === 'workflow' || v === 'workflows') return { kind: 'workflows' };
  if (v === 'atlas') return { kind: 'atlas' };
  if (v === 'all') return { kind: 'all' };
  if (v.startsWith('wf:')) return { kind: 'specific', sourceId: value.slice(3) };
  if (v.startsWith('atlas:')) return { kind: 'specific', sourceId: value.slice(6) };
  return null;
}

function sourceTypeFromValue(value: string): SearchSourceType | null {
  const v = value.toLowerCase();
  for (const st of SOURCE_TYPE_VALUES) {
    if (st === v) return st;
  }
  return null;
}

/**
 * Split input on whitespace honouring quoted spans. Returns tokens
 * with quotes preserved on phrases.
 */
function tokenize(input: string): string[] {
  const tokens: string[] = [];
  let buf = '';
  let inQuote = false;
  for (const ch of input) {
    if (ch === '"') {
      buf += ch;
      if (inQuote) {
        tokens.push(buf);
        buf = '';
      }
      inQuote = !inQuote;
      continue;
    }
    if (!inQuote && /\s/.test(ch)) {
      if (buf) tokens.push(buf);
      buf = '';
      continue;
    }
    buf += ch;
  }
  if (buf) tokens.push(buf);
  return tokens;
}
